import { Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { map, Observable, startWith } from 'rxjs';
import { CustomersFilterDialogData } from '../../../interfaces/customers-filter-dialog-data';
import { ZoneItem } from '../../../interfaces/lookup-tables/zone.model';
import { AgenteLookupItem } from '../../../services/lookup-tables/agenti.service';
import { IspettoreLookupItem } from '../../../services/lookup-tables/ispettori.service';

@Component({
  selector: 'cus-customers-filter-dialog-component',
  imports: [
    CommonModule, MatDialogModule, MatButtonModule,
    ReactiveFormsModule, FormsModule,
    MatFormFieldModule, MatInputModule, MatIconModule,
    MatCheckboxModule, MatAutocompleteModule,
    MatDatepickerModule, MatNativeDateModule,
  ],
  templateUrl: './customers-filter-dialog-component.html',
  styleUrl: './customers-filter-dialog-component.scss',
})
export class CustomersFilterDialogComponent {
  filteredLocalita!: Observable<string[]>;
  filteredZona!: Observable<ZoneItem[]>;
  filteredTipoZona!: Observable<string[]>;
  filteredAgente!: Observable<AgenteLookupItem[]>;
  filteredIspettore!: Observable<IspettoreLookupItem[]>;

  filterForm: FormGroup;
  protected dialogData = inject<CustomersFilterDialogData>(MAT_DIALOG_DATA);

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<CustomersFilterDialogComponent>,
  )
  {
    this.filterForm = this.formBuilder.group({
      searchText: [this.dialogData.value.searchText ?? ''],
      localita: [{ value: this.dialogData.value.localita ?? '', disabled: true }],
      ispettore: [this.dialogData.value.ispettore ?? ''],
      ispettoreId: [this.dialogData.value.ispettoreId ?? ''],
      zona: [this.dialogData.value.zona ?? ''],
      zonaId: [this.dialogData.value.zonaId ?? ''],
      tipoZona: [{ value: this.dialogData.value.tipoZona ?? [], disabled: true }],
      agente: [this.dialogData.value.agente ?? ''],
      agenteId: [this.dialogData.value.agenteId ?? ''],
      dataUltimaFattura: [this.dialogData.value.dataUltimaFattura ?? null],
      newCustomer: [{ value: this.dialogData.value.newCustomer ?? false, disabled: true }],
    });

    const filterFn = (opts: string[], value: string | string[] | null) => {
      const search = Array.isArray(value) ? value.join(' ') : (value ?? '');
      return opts.filter(option => option.toLowerCase().includes(search.toLowerCase()));
    };

    this.filteredLocalita = this.filterForm.get('localita')!.valueChanges.pipe(
      startWith(this.filterForm.get('localita')!.value ?? ''),
      map(value => filterFn(this.dialogData.options.localita, value))
    );

    this.filteredZona = this.filterForm.get('zona')!.valueChanges.pipe(
      startWith(this.filterForm.get('zona')!.value ?? ''),
      map(value => this.filterZoneOptions(value))
    );

    this.filterForm.get('zona')!.valueChanges.subscribe(value => {
      const selectedZone = this.dialogData.options.zona.find(zone => zone.id === this.filterForm.get('zonaId')!.value);
      if (selectedZone?.nome !== value) {
        this.filterForm.get('zonaId')!.setValue('', { emitEvent: false });
      }
    });

    this.filteredTipoZona = this.filterForm.get('tipoZona')!.valueChanges.pipe(
      startWith(this.filterForm.get('tipoZona')!.value ?? []),
      map(value => filterFn(this.dialogData.options.tipoZona, value))
    );

    this.filteredAgente = this.filterForm.get('agente')!.valueChanges.pipe(
      startWith(this.filterForm.get('agente')!.value ?? ''),
      map(value => this.filterLookupOptions(this.dialogData.options.agente, value))
    );

    this.filteredIspettore = this.filterForm.get('ispettore')!.valueChanges.pipe(
      startWith(this.filterForm.get('ispettore')!.value ?? ''),
      map(value => this.filterLookupOptions(this.dialogData.options.ispettore, value))
    );

    this.filterForm.get('agente')!.valueChanges.subscribe(value => {
      const selectedAgente = this.dialogData.options.agente.find(option => option.value === this.filterForm.get('agenteId')!.value);
      if (selectedAgente?.label !== value) {
        this.filterForm.get('agenteId')!.setValue('', { emitEvent: false });
      }
    });

    this.filterForm.get('ispettore')!.valueChanges.subscribe(value => {
      const selectedIspettore = this.dialogData.options.ispettore.find(option => option.value === this.filterForm.get('ispettoreId')!.value);
      if (selectedIspettore?.label !== value) {
        this.filterForm.get('ispettoreId')!.setValue('', { emitEvent: false });
      }
    });
  }

  onClose() {
    this.dialogRef.close();
  }

  onCancelFilter() {
    this.dialogRef.close({ action: 'reset' });
  }

  onConfirmFilter() {
    this.dialogRef.close({ action: 'apply', value: this.filterForm.getRawValue() });
  }

  onZonaSelected(zone: ZoneItem) {
    this.filterForm.patchValue({
      zona: zone.nome,
      zonaId: zone.id,
    });
  }

  onAgenteSelected(agente: AgenteLookupItem) {
    this.filterForm.patchValue({
      agente: agente.label,
      agenteId: agente.value,
    });
  }

  onIspettoreSelected(ispettore: IspettoreLookupItem) {
    this.filterForm.patchValue({
      ispettore: ispettore.label,
      ispettoreId: ispettore.value,
    });
  }

  private filterZoneOptions(value: string | null): ZoneItem[] {
    const search = value ?? '';
    return this.dialogData.options.zona.filter(option =>
      option.nome.toLowerCase().includes(search.toLowerCase())
    );
  }

  private filterLookupOptions<T extends { label: string }>(options: T[], value: string | string[] | null): T[] {
    const search = Array.isArray(value) ? value.join(' ') : (value ?? '');
    return options.filter(option => option.label.toLowerCase().includes(search.toLowerCase()));
  }
}
